import { z } from 'zod';

import type { HoldingUnitEvent } from '../holding-units/holding-unit-event.dto.js';
import { HoldingUnitEventSchema } from '../holding-units/holding-unit-event.schema.js';
import { HoldingUnitEventEntityTypeSchema } from '../holding-units/holding-unit-event-envelope.schema.js';
import { TareProfileDtoSchema } from '../tare-profiles/tare-profiles.schema.js';
import { UnitProfileDtoSchema } from '../unit-profiles/unit-profiles.schema.js';
import type {
  HoldingUnitsCanonicalBootstrapConfiguration,
  HoldingUnitsCanonicalBootstrapCursor,
  HoldingUnitsCanonicalBootstrapPosition,
  HoldingUnitsCanonicalBootstrapRequest,
  HoldingUnitsCanonicalBootstrapRestartRequiredDetails,
  HoldingUnitsCanonicalBootstrapResponse,
  HoldingUnitsCanonicalDeltaRequest,
  HoldingUnitsCanonicalDeltaResponse,
  HoldingUnitsCanonicalEventRecord,
  HoldingUnitsCanonicalRecord,
  HoldingUnitsCanonicalStreamAck,
  HoldingUnitsCanonicalStreamCatchup,
  HoldingUnitsCanonicalModuleCursor,
  HoldingUnitSyncPayload,
  HoldingUnitSyncPayloadV5,
} from './holding-units-sync.dto.js';
import {
  HOLDING_UNITS_CANONICAL_BOOTSTRAP_MAX_RECORDS,
  HOLDING_UNITS_CANONICAL_BOOTSTRAP_MODULE,
  HOLDING_UNITS_CANONICAL_BOOTSTRAP_PROTOCOL_VERSION,
  HOLDING_UNITS_CANONICAL_BOOTSTRAP_RESTART_ACTION,
  holdingUnitSyncEventTypeValues,
} from './holding-units-sync.dto.js';
import {
  HoldingUnitsCanonicalOuterSequenceSchema,
  HoldingUnitsCanonicalSnapshotRecordSchema,
} from './holding-units-canonical-snapshot.schema.js';
import { SyncClientIdentitySchema } from './sync-common.schema.js';
import type { HoldingUnitSyncEvent } from './sync.dto.js';

export const HoldingUnitSyncEventTypeSchema = z.enum(holdingUnitSyncEventTypeValues);

const HoldingUnitSyncPayloadV5Schema: z.ZodType<HoldingUnitSyncPayloadV5> = z
  .object({
    schemaVersion: z.literal(5),
    events: z.array(HoldingUnitEventSchema).nonempty(),
  })
  .strict();

export const HoldingUnitSyncPayloadSchema: z.ZodType<HoldingUnitSyncPayload> =
  HoldingUnitSyncPayloadV5Schema;

export function buildHoldingUnitSyncPayload(input: {
  events: ReadonlyArray<HoldingUnitEvent>;
}): HoldingUnitSyncPayload {
  return HoldingUnitSyncPayloadSchema.parse({
    schemaVersion: 5,
    events: [...input.events],
  });
}

export const HoldingUnitSyncEventSchema: z.ZodType<HoldingUnitSyncEvent> = z.object({
  eventId: z.uuid(),
  entityType: z.literal('holdingUnit'),
  entityId: z.uuid(),
  eventType: HoldingUnitSyncEventTypeSchema,
  payload: HoldingUnitSyncPayloadSchema,
  occurredAt: z.iso.datetime(),
  source: z.string().trim().min(1).nullable().optional(),
});

export const HoldingUnitsCanonicalBootstrapPositionSchema: z.ZodType<HoldingUnitsCanonicalBootstrapPosition> =
  z
    .object({
      outerSequence: HoldingUnitsCanonicalOuterSequenceSchema,
      eventId: z.uuid(),
    })
    .strict();

export const HoldingUnitsCanonicalBootstrapCursorSchema: z.ZodType<HoldingUnitsCanonicalBootstrapCursor> =
  z
    .object({
      protocolVersion: z.literal(HOLDING_UNITS_CANONICAL_BOOTSTRAP_PROTOCOL_VERSION),
      module: z.literal(HOLDING_UNITS_CANONICAL_BOOTSTRAP_MODULE),
      sessionId: z.uuid(),
      watermark: HoldingUnitsCanonicalOuterSequenceSchema,
      watermarkEventId: z.uuid().nullable(),
      after: HoldingUnitsCanonicalBootstrapPositionSchema.nullable(),
    })
    .strict()
    .superRefine((value, ctx) => {
      if ((value.watermark === '0') !== (value.watermarkEventId === null)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Zero watermark requires no event ID; a positive watermark requires one.',
          path: ['watermarkEventId'],
        });
      }
      if (
        value.after &&
        compareCanonicalPositions(value.after, {
          outerSequence: value.watermark,
          eventId: value.watermarkEventId ?? '',
        }) > 0
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Bootstrap cursor position cannot exceed its watermark.',
          path: ['after', 'outerSequence'],
        });
      }
    });

export const HoldingUnitsCanonicalStreamAckSchema: z.ZodType<HoldingUnitsCanonicalStreamAck> = z
  .object({
    entityType: HoldingUnitEventEntityTypeSchema,
    entityId: z.uuid(),
    streamVersion: z.number().int().positive(),
    lastEventId: z.uuid(),
  })
  .strict();

export const HoldingUnitsCanonicalModuleCursorSchema: z.ZodType<HoldingUnitsCanonicalModuleCursor> =
  z
    .object({
      protocolVersion: z.literal(HOLDING_UNITS_CANONICAL_BOOTSTRAP_PROTOCOL_VERSION),
      module: z.literal(HOLDING_UNITS_CANONICAL_BOOTSTRAP_MODULE),
      generationId: z.uuid(),
      outerSequence: HoldingUnitsCanonicalOuterSequenceSchema,
      eventId: z.uuid().nullable(),
      pendingStreamAcks: z
        .array(HoldingUnitsCanonicalStreamAckSchema)
        .max(HOLDING_UNITS_CANONICAL_BOOTSTRAP_MAX_RECORDS),
    })
    .strict()
    .superRefine((value, ctx) => {
      if ((value.outerSequence === '0') !== (value.eventId === null)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Zero sequence requires no event ID; a positive sequence requires one.',
          path: ['eventId'],
        });
      }

      const keys = new Set<string>();
      for (const [index, ack] of value.pendingStreamAcks.entries()) {
        const key = `${ack.entityType}:${ack.entityId}`;
        if (keys.has(key)) {
          ctx.addIssue({
            code: 'custom',
            message: 'Pending stream acknowledgements must be unique by stream.',
            path: ['pendingStreamAcks', index],
          });
        }
        keys.add(key);
      }
    });

export const HoldingUnitsCanonicalEventRecordSchema: z.ZodType<HoldingUnitsCanonicalEventRecord> = z
  .object({
    kind: z.literal('event'),
    outerSequence: HoldingUnitsCanonicalOuterSequenceSchema,
    event: HoldingUnitEventSchema,
  })
  .strict();

export const HoldingUnitsCanonicalRecordSchema: z.ZodType<HoldingUnitsCanonicalRecord> = z.union([
  HoldingUnitsCanonicalSnapshotRecordSchema,
  HoldingUnitsCanonicalEventRecordSchema,
]);

export const HoldingUnitsCanonicalBootstrapConfigurationSchema: z.ZodType<HoldingUnitsCanonicalBootstrapConfiguration> =
  z
    .object({
      tareProfiles: z.array(TareProfileDtoSchema),
      unitProfiles: z.array(UnitProfileDtoSchema),
    })
    .strict();

export const HoldingUnitsCanonicalBootstrapRequestSchema: z.ZodType<HoldingUnitsCanonicalBootstrapRequest> =
  z
    .object({
      client: SyncClientIdentitySchema,
      cursor: HoldingUnitsCanonicalBootstrapCursorSchema.nullable(),
    })
    .strict();

export const HoldingUnitsCanonicalBootstrapRestartRequiredDetailsSchema: z.ZodType<HoldingUnitsCanonicalBootstrapRestartRequiredDetails> =
  z
    .object({
      module: z.literal(HOLDING_UNITS_CANONICAL_BOOTSTRAP_MODULE),
      action: z.literal(HOLDING_UNITS_CANONICAL_BOOTSTRAP_RESTART_ACTION),
      sessionId: z.uuid(),
    })
    .strict();

export const HoldingUnitsCanonicalBootstrapResponseSchema: z.ZodType<HoldingUnitsCanonicalBootstrapResponse> =
  z
    .object({
      protocolVersion: z.literal(HOLDING_UNITS_CANONICAL_BOOTSTRAP_PROTOCOL_VERSION),
      module: z.literal(HOLDING_UNITS_CANONICAL_BOOTSTRAP_MODULE),
      sessionId: z.uuid(),
      watermark: HoldingUnitsCanonicalOuterSequenceSchema,
      watermarkEventId: z.uuid().nullable(),
      configuration: HoldingUnitsCanonicalBootstrapConfigurationSchema.nullable(),
      records: z
        .array(HoldingUnitsCanonicalRecordSchema)
        .max(HOLDING_UNITS_CANONICAL_BOOTSTRAP_MAX_RECORDS),
      nextCursor: HoldingUnitsCanonicalBootstrapCursorSchema.nullable(),
      moduleCursor: HoldingUnitsCanonicalModuleCursorSchema.nullable(),
      complete: z.boolean(),
    })
    .strict()
    .superRefine((value, ctx) => {
      if ((value.watermark === '0') !== (value.watermarkEventId === null)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Zero watermark requires no event ID; a positive watermark requires one.',
          path: ['watermarkEventId'],
        });
      }

      validateOrderedRecords(value.records, ctx, ['records']);
      const watermark = {
        outerSequence: value.watermark,
        eventId: value.watermarkEventId ?? '',
      };
      for (const [index, record] of value.records.entries()) {
        if (compareCanonicalPositions(canonicalRecordPosition(record), watermark) > 0) {
          ctx.addIssue({
            code: 'custom',
            message: 'Bootstrap record cannot exceed the captured watermark.',
            path: ['records', index],
          });
        }
      }

      if (value.complete) {
        if (value.nextCursor !== null) {
          ctx.addIssue({
            code: 'custom',
            message: 'A complete bootstrap cannot return a next page cursor.',
            path: ['nextCursor'],
          });
        }
        if (
          value.moduleCursor === null ||
          value.moduleCursor.generationId !== value.sessionId ||
          value.moduleCursor.outerSequence !== value.watermark ||
          value.moduleCursor.eventId !== value.watermarkEventId ||
          value.moduleCursor.pendingStreamAcks.length !== 0
        ) {
          ctx.addIssue({
            code: 'custom',
            message: 'A complete bootstrap must return its session cursor at the watermark.',
            path: ['moduleCursor'],
          });
        }
        return;
      }

      if (value.moduleCursor !== null) {
        ctx.addIssue({
          code: 'custom',
          message: 'An incomplete bootstrap cannot return a durable module cursor.',
          path: ['moduleCursor'],
        });
      }
      const last = value.records.at(-1);
      if (!value.nextCursor || !last || !value.nextCursor.after) {
        ctx.addIssue({
          code: 'custom',
          message: 'An incomplete bootstrap requires a non-empty page and next cursor.',
          path: ['nextCursor'],
        });
        return;
      }
      if (
        value.nextCursor.sessionId !== value.sessionId ||
        value.nextCursor.watermark !== value.watermark ||
        value.nextCursor.watermarkEventId !== value.watermarkEventId ||
        compareCanonicalPositions(value.nextCursor.after, canonicalRecordPosition(last)) !== 0
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Next page cursor must point at the final ordered record in this session.',
          path: ['nextCursor'],
        });
      }
    });

export const HoldingUnitsCanonicalStreamCatchupSchema: z.ZodType<HoldingUnitsCanonicalStreamCatchup> =
  z
    .object({
      entityType: HoldingUnitEventEntityTypeSchema,
      entityId: z.uuid(),
      snapshot: HoldingUnitsCanonicalSnapshotRecordSchema.nullable(),
      tail: z
        .array(HoldingUnitsCanonicalEventRecordSchema)
        .max(HOLDING_UNITS_CANONICAL_BOOTSTRAP_MAX_RECORDS),
    })
    .strict()
    .superRefine((value, ctx) => {
      if (!value.snapshot && value.tail.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'A stream catch-up requires a snapshot or real event tail.',
          path: ['tail'],
        });
        return;
      }

      let expectedVersion = 1;
      let previousPosition: HoldingUnitsCanonicalBootstrapPosition | null = null;
      if (value.snapshot) {
        const snapshot = value.snapshot.snapshot;
        if (snapshot.entityType !== value.entityType || snapshot.entityId !== value.entityId) {
          ctx.addIssue({
            code: 'custom',
            message: 'Catch-up snapshot must belong to the declared stream.',
            path: ['snapshot'],
          });
        }
        expectedVersion = snapshot.baseStreamVersion + 1;
        previousPosition = canonicalRecordPosition(value.snapshot);
      }

      for (const [index, record] of value.tail.entries()) {
        if (
          record.event.entityType !== value.entityType ||
          record.event.entityId !== value.entityId
        ) {
          ctx.addIssue({
            code: 'custom',
            message: 'Catch-up tail events must belong to the declared stream.',
            path: ['tail', index, 'event'],
          });
        }
        if (record.event.streamVersion !== expectedVersion) {
          ctx.addIssue({
            code: 'custom',
            message: 'Catch-up tail must be contiguous after its canonical base.',
            path: ['tail', index, 'event', 'streamVersion'],
          });
        }
        if (value.snapshot && record.event.streamVersion > value.snapshot.headStreamVersion) {
          ctx.addIssue({
            code: 'custom',
            message: 'Catch-up tail cannot exceed the manifest stream head.',
            path: ['tail', index, 'event', 'streamVersion'],
          });
        }
        const position = canonicalRecordPosition(record);
        if (previousPosition && compareCanonicalPositions(position, previousPosition) <= 0) {
          ctx.addIssue({
            code: 'custom',
            message: 'Catch-up records must be strictly ordered by canonical position.',
            path: ['tail', index],
          });
        }
        expectedVersion += 1;
        previousPosition = position;
      }

      if (value.snapshot) {
        const reachedStreamVersion =
          value.tail.at(-1)?.event.streamVersion ?? value.snapshot.snapshot.baseStreamVersion;
        if (reachedStreamVersion !== value.snapshot.headStreamVersion) {
          ctx.addIssue({
            code: 'custom',
            message: 'Catch-up snapshot and tail must reach the manifest stream head.',
            path: ['snapshot', 'headStreamVersion'],
          });
        }
      }
    });

export const HoldingUnitsCanonicalDeltaRequestSchema: z.ZodType<HoldingUnitsCanonicalDeltaRequest> =
  z
    .object({
      client: SyncClientIdentitySchema,
      cursor: HoldingUnitsCanonicalModuleCursorSchema,
    })
    .strict();

export const HoldingUnitsCanonicalDeltaResponseSchema: z.ZodType<HoldingUnitsCanonicalDeltaResponse> =
  z
    .object({
      protocolVersion: z.literal(HOLDING_UNITS_CANONICAL_BOOTSTRAP_PROTOCOL_VERSION),
      module: z.literal(HOLDING_UNITS_CANONICAL_BOOTSTRAP_MODULE),
      streamCatchups: z
        .array(HoldingUnitsCanonicalStreamCatchupSchema)
        .max(HOLDING_UNITS_CANONICAL_BOOTSTRAP_MAX_RECORDS),
      records: z
        .array(HoldingUnitsCanonicalEventRecordSchema)
        .max(HOLDING_UNITS_CANONICAL_BOOTSTRAP_MAX_RECORDS),
      nextCursor: HoldingUnitsCanonicalModuleCursorSchema,
      hasMore: z.boolean(),
    })
    .strict()
    .superRefine((value, ctx) => {
      validateOrderedRecords(value.records, ctx, ['records']);
      const pageRecords: HoldingUnitsCanonicalRecord[] = [
        ...value.records,
        ...value.streamCatchups.flatMap((catchup) => [
          ...(catchup.snapshot ? [catchup.snapshot] : []),
          ...catchup.tail,
        ]),
      ];
      if (pageRecords.length > HOLDING_UNITS_CANONICAL_BOOTSTRAP_MAX_RECORDS) {
        ctx.addIssue({
          code: 'custom',
          message: 'A canonical delta page cannot exceed the record-count limit.',
          path: ['records'],
        });
      }

      const eventIds = new Set<string>();
      for (const record of pageRecords) {
        const eventId = canonicalRecordPosition(record).eventId;
        if (eventIds.has(eventId)) {
          ctx.addIssue({
            code: 'custom',
            message: 'A canonical delta page cannot repeat a real event ID.',
            path: ['records'],
          });
        }
        eventIds.add(eventId);
      }

      const finalRecord = pageRecords.reduce<HoldingUnitsCanonicalRecord | null>(
        (current, record) =>
          !current ||
          compareCanonicalPositions(
            canonicalRecordPosition(record),
            canonicalRecordPosition(current),
          ) > 0
            ? record
            : current,
        null,
      );
      if (
        finalRecord &&
        compareCanonicalPositions(
          {
            outerSequence: value.nextCursor.outerSequence,
            eventId: value.nextCursor.eventId ?? '',
          },
          canonicalRecordPosition(finalRecord),
        ) !== 0
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Delta cursor must advance to the final canonical record in the page.',
          path: ['nextCursor', 'outerSequence'],
        });
      }
      if (value.hasMore && !finalRecord) {
        ctx.addIssue({
          code: 'custom',
          message: 'A delta page with more records cannot be empty.',
          path: ['hasMore'],
        });
      }

      const expectedAcks = value.streamCatchups
        .map((catchup) => {
          const tail = catchup.tail.at(-1);
          const streamVersion =
            tail?.event.streamVersion ?? catchup.snapshot?.snapshot.baseStreamVersion;
          const lastEventId = tail?.event.eventId ?? catchup.snapshot?.snapshot.lastEventId;
          return `${catchup.entityType}:${catchup.entityId}:${String(streamVersion)}:${String(lastEventId)}`;
        })
        .sort();
      const actualAcks = value.nextCursor.pendingStreamAcks
        .map(
          (ack) =>
            `${ack.entityType}:${ack.entityId}:${String(ack.streamVersion)}:${ack.lastEventId}`,
        )
        .sort();
      if (expectedAcks.join('|') !== actualAcks.join('|')) {
        ctx.addIssue({
          code: 'custom',
          message: 'Delta cursor acknowledgements must match the returned stream catch-ups.',
          path: ['nextCursor', 'pendingStreamAcks'],
        });
      }
    });

export function canonicalRecordPosition(
  record: HoldingUnitsCanonicalRecord,
): HoldingUnitsCanonicalBootstrapPosition {
  return record.kind === 'event'
    ? { outerSequence: record.outerSequence, eventId: record.event.eventId }
    : {
        outerSequence: record.snapshot.baseOuterSequence,
        eventId: record.snapshot.lastEventId,
      };
}

function validateOrderedRecords(
  records: ReadonlyArray<HoldingUnitsCanonicalRecord>,
  ctx: z.RefinementCtx,
  path: Array<string | number>,
): void {
  type StreamState = {
    snapshotStreamVersions: Map<string, number>;
    snapshotStreamHeads: Map<string, number>;
    eventStreams: Set<string>;
  };
  const statesByEntityType: Partial<Record<HoldingUnitEvent['entityType'], StreamState>> = {};
  let previousOuterSequence: string | null = null;
  let previousEventId = '';
  for (const [index, record] of records.entries()) {
    const outerSequence =
      record.kind === 'snapshot' ? record.snapshot.baseOuterSequence : record.outerSequence;
    const eventId = record.kind === 'snapshot' ? record.snapshot.lastEventId : record.event.eventId;
    if (
      previousOuterSequence !== null &&
      compareCanonicalPositionValues(
        outerSequence,
        eventId,
        previousOuterSequence,
        previousEventId,
      ) <= 0
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Canonical records must be strictly ordered by outer sequence and event ID.',
        path: [...path, index],
      });
    }
    previousOuterSequence = outerSequence;
    previousEventId = eventId;

    const entityType =
      record.kind === 'snapshot' ? record.snapshot.entityType : record.event.entityType;
    const entityId = record.kind === 'snapshot' ? record.snapshot.entityId : record.event.entityId;
    const streamState = (statesByEntityType[entityType] ??= {
      snapshotStreamVersions: new Map<string, number>(),
      snapshotStreamHeads: new Map<string, number>(),
      eventStreams: new Set<string>(),
    });

    if (record.kind === 'snapshot') {
      if (
        streamState.snapshotStreamVersions.has(entityId) ||
        streamState.eventStreams.has(entityId)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'A canonical page cannot repeat or place a stream snapshot after its tail.',
          path: [...path, index],
        });
      }
      streamState.snapshotStreamVersions.set(entityId, record.snapshot.baseStreamVersion);
      streamState.snapshotStreamHeads.set(entityId, record.headStreamVersion);
      continue;
    }

    streamState.eventStreams.add(entityId);
    const previousVersion = streamState.snapshotStreamVersions.get(entityId);
    if (previousVersion !== undefined) {
      const transferHead = streamState.snapshotStreamHeads.get(entityId);
      if (record.event.streamVersion !== previousVersion + 1) {
        ctx.addIssue({
          code: 'custom',
          message: 'Real event tail must be contiguous after its snapshot base.',
          path: [...path, index, 'event', 'streamVersion'],
        });
      }
      if (transferHead !== undefined && record.event.streamVersion > transferHead) {
        ctx.addIssue({
          code: 'custom',
          message: 'Real event tail cannot exceed the manifest stream head.',
          path: [...path, index, 'event', 'streamVersion'],
        });
      }
      streamState.snapshotStreamVersions.set(entityId, record.event.streamVersion);
    }
  }
}

function compareCanonicalPositions(
  left: HoldingUnitsCanonicalBootstrapPosition,
  right: HoldingUnitsCanonicalBootstrapPosition,
): number {
  return compareCanonicalPositionValues(
    left.outerSequence,
    left.eventId,
    right.outerSequence,
    right.eventId,
  );
}

function compareCanonicalPositionValues(
  leftOuterSequence: string,
  leftEventId: string,
  rightOuterSequence: string,
  rightEventId: string,
): number {
  if (leftOuterSequence.length !== rightOuterSequence.length) {
    return leftOuterSequence.length < rightOuterSequence.length ? -1 : 1;
  }
  if (leftOuterSequence !== rightOuterSequence) {
    return leftOuterSequence < rightOuterSequence ? -1 : 1;
  }
  return leftEventId < rightEventId ? -1 : leftEventId > rightEventId ? 1 : 0;
}
